import { formatDateTime } from '@seen/core/dates'
import {
  type AbsenceProvenance,
  type CitedSource,
  type ClinicianProvenance,
  type PassageProvenance,
  type SourceKind,
  type SuggestionProvenance,
  sourceKindOfReference,
} from '@seen/core/models/compositionNote'
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
} from '@seen/ui'
import type { TablerIcon } from '@tabler/icons-react'
import {
  IconChevronRight,
  IconMessage,
  IconQuote,
  IconSearch,
} from '@tabler/icons-react'
import {
  type JSX,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { sourceMentionHtml } from '#src/components/rich-text-editor/sourceMentionHtml.ts'
import { RichText } from '#src/components/RichText.tsx'
import { useResolvedSuggestionSource } from '#src/hooks/useResolvedSuggestionSource.ts'

export interface SuggestionSourceExcerptProps {
  /* Where the suggestion came from: the resource it drew on, and the model's
   * excerpt of that resource when it quoted one. Only the reference is used to
   * open the chip — its text is derived from the resource. Absent on a
   * suggestion that cited nothing, which renders no disclosure at all.
   *
   * The fallback for a record with no `sourceProvenance`: a record staged
   * before the server started creating provenance carries only this, and must keep
   * rendering exactly as it always has. */
  citation?: CitedSource
  /* Where the suggestion is grounded, filled in by the server: one entry per
   * passage it rests on, in the order the model cited them, or a lone absence
   * claim. Takes precedence over `citation` when present — a record can carry
   * both once the writer transitions, and the newer, richer field wins. An
   * empty list is treated as absent. */
  sourceProvenance?: SuggestionProvenance[]
  className?: string
  /* Deep-links the clinician chip to the chat turn it names. `@seen/react` may
   * not depend on `apps/portal`, where the thread switcher lives, so this is
   * the seam: supplied, the chip is a real button; omitted, it renders as
   * plain text. No caller passes this yet: the server creates `clinician`
   * provenance, but opening the named turn from a chip is not wired up. */
  onOpenSourceThread?: OpenSourceThread
}

type OpenSourceThread = (target: {
  reference: string
  messageId?: string
}) => void

/* Where an AI suggestion came from, offered so the clinician can check the
 * proposal against its source before deciding it: the cited resource as a
 * mention chip, and the model's quoted excerpt of it when there is one. One
 * component for every kind of suggestion — a care-plan answer atom and an
 * inline note suggestion render the same disclosure — so the citation reads the
 * same wherever a proposal appears.
 *
 * The chip names the source exactly as the participant's notes list names it,
 * with the date it was signed, because the label is derived from the resolved
 * resource rather than taken from the model. Nothing at all is shown — not even
 * the quote — for a source that is unsigned, gone, still resolving, or on
 * another participant's chart; see `useResolvedSuggestionSource`. A quote is a
 * citation, so it never appears without the record it was taken from.
 *
 * The chip is prefixed with the kind of source it is — this visit, a chart
 * record, or carried forward from an older document — because a value carried
 * forward may be stale where the other two are current. An absence claim lists
 * what was searched, not a source, so it carries no prefix.
 *
 * It is NOT part of the proposal: an accept writes the proposal's text and
 * discards this, which never reaches the record. The chip lives here rather
 * than in the proposal body for that reason — a citation inside the body was
 * accepted into the answer along with the text it supported. The trigger is
 * written to say all of that ("excerpt" and the quote mark say quoted rather
 * than proposed, "Source" says it came from what the assistant read, "not
 * saved" says an accept drops it), because the disclosure can be closed.
 *
 * It carries its own top margin rather than taking spacing from each caller,
 * so the gap under the proposal comes from one place instead of drifting per
 * surface.
 *
 * A suggestion resting on several passages gets one disclosure for all of
 * them rather than one per passage, so a two-sentence summary does not grow a
 * stack of folds under it: each passage is listed inside as its quote followed
 * by its chip, and each passage keeps the single-source rule on its own — one
 * that does not resolve is left out, quote and all, and a list none of whose
 * entries can be shown renders nothing.
 *
 * Open by default, so the source is in view beside the proposal it supports
 * without a click per atom; the clinician can fold it away. Radix's
 * Collapsible, the disclosure the rest of the product uses, supplies the
 * trigger's button semantics and its `aria-expanded`/`aria-controls` pair, so
 * a keyboard reaches it by Tab and a screen reader announces both the label
 * and the state. */
export function SuggestionSourceExcerpt({
  citation,
  sourceProvenance,
  className,
  onOpenSourceThread,
}: SuggestionSourceExcerptProps): JSX.Element | null {
  // Provenance wins; citation is the pre-provenance fallback.
  if (sourceProvenance && sourceProvenance.length > 1) {
    return (
      <SourceListExcerpt
        className={className}
        entries={sourceProvenance}
        onOpenSourceThread={onOpenSourceThread}
      />
    )
  }
  const only = sourceProvenance?.[0]
  if (only) {
    if (only.kind === 'passage') {
      /* The passage rendering IS the citation rendering: same reference, same
       * quote, same chip. `versionId` is carried on the provenance but nothing
       * here reads it. */
      return (
        <CitedSourceExcerpt
          citation={{ reference: only.reference, quote: only.quote }}
          sourceKind={sourceKindOf(only)}
          className={className}
        />
      )
    }
    if (only.kind === 'absence') {
      return <AbsenceSourceExcerpt provenance={only} className={className} />
    }
    if (only.kind === 'clinician') {
      return (
        <ClinicianSourceExcerpt
          provenance={only}
          className={className}
          onOpenSourceThread={onOpenSourceThread}
        />
      )
    }
    /* `region` — a citation whose evidence is a crop of a scanned page — has
     * no rendering here yet, and nothing creates it, so this is unreachable
     * today. `clinician` is matched explicitly above so that any kind without
     * a rendering lands here and shows nothing, rather than falling into the
     * clinician arm and being rendered as a clinician's chat message. */
    return null
  }

  /* Resolving the cited record needs the Medplum client, so the branch that
   * reads it is a separate component mounted only when there is a citation to
   * resolve. A proposal that cited nothing must not depend on it. */
  if (!citation) {
    return null
  }
  return (
    <CitedSourceExcerpt
      citation={citation}
      sourceKind={sourceKindOfReference(citation.reference)}
      className={className}
    />
  )
}

const SOURCE_KIND_PREFIX: Record<SourceKind, string> = {
  'this-visit': 'This visit',
  'chart-record': 'Chart record',
  'carried-forward': 'Carried forward',
}

/* The kind the server stamped on the entry, or the one its reference implies
 * for a record staged before it stamped any. */
function sourceKindOf(
  entry: PassageProvenance | ClinicianProvenance
): SourceKind {
  return entry.sourceKind ?? sourceKindOfReference(entry.reference)
}

/* The chip with its kind in front, as plain text beside the mention rather
 * than inside its label: the participant and immunization chips draw their own
 * name and ignore the derived label, so a prefix carried in the label would be
 * missing from exactly those. Kept in the chip's own line so a screen reader
 * announces the kind and the name together. */
function SourceMention({
  reference,
  label,
  sourceKind,
}: {
  reference: string
  label: string
  sourceKind: SourceKind
}): JSX.Element {
  return (
    <>
      <span>{SOURCE_KIND_PREFIX[sourceKind]} · </span>
      <RichText inline value={sourceMentionHtml(reference, label)} />
    </>
  )
}

/* A proposal that named a source: the chip's text comes from reading it. */
function CitedSourceExcerpt({
  citation,
  sourceKind,
  className,
}: {
  citation: CitedSource
  sourceKind: SourceKind
  className?: string
}): JSX.Element | null {
  const { label } = useResolvedSuggestionSource(citation)

  /* Nothing renders until the source has resolved to a signed record on this
   * participant's chart — not the chip, and not the quote either. The quote is
   * copied out of the cited record, so an unsigned draft, a record that could
   * not be read, another participant's note, or a read still in flight would
   * all put words on screen that cannot be cited; a quote is a citation. An
   * unsigned source is never citable, so a proposal that cited one shows no
   * source at all rather than its quote under a missing chip. */
  if (!label) {
    return null
  }

  return (
    <SourceDisclosure
      className={className}
      triggerLabel={citation.quote ? 'Source excerpt' : 'Source'}
      caption={
        citation.quote
          ? 'Quoted, not saved when you accept'
          : 'Not saved when you accept'
      }
      quote={citation.quote}
    >
      {/* A real mention, parsed by the same node the note renders citations
          with, so it opens the source the way a chip in the note does. */}
      <SourceChip>
        <SourceMention
          reference={citation.reference}
          label={label}
          sourceKind={sourceKind}
        />
      </SourceChip>
    </SourceDisclosure>
  )
}

/* The suggestion asserts something is absent from the chart: no quote to show
 * — nothing was found to quote — so the disclosure names how many records were
 * actually searched instead, taken from `searched.length` rather than from how
 * many of the chips below manage to resolve. The trigger says "Searched" (an
 * action completed) rather than a bare "Not found", which reads as
 * indistinguishable from a source that failed to load — the claim here is that
 * the search happened and came up empty, not that something is missing or
 * broken. `question` is what was asked, shown above the chips so the claim
 * reads before its evidence; without it the disclosure asserts a negative the
 * clinician has no way to evaluate. It renders as plain text, never a
 * `<blockquote>` — a question the assistant posed was not copied out of a
 * record, and dressing it as a quotation would claim otherwise. */
function AbsenceSourceExcerpt({
  provenance,
  className,
}: {
  provenance: AbsenceProvenance
  className?: string
}): JSX.Element {
  const count = provenance.searched.length
  return (
    <SourceDisclosure
      className={className}
      icon={IconSearch}
      triggerLabel="Searched, not found"
      caption={
        count > 0
          ? `Looked in ${count} record${count === 1 ? '' : 's'}, not saved when you accept`
          : 'Found no records to look in, not saved when you accept'
      }
    >
      <SourceChip>
        <p className="text-xs">
          <span className="font-medium text-foreground">Checked for: </span>
          <span className="text-muted-foreground">{provenance.question}</span>
        </p>
        {count > 0 && (
          <ul className="flex flex-col gap-1">
            {provenance.searched.map((record) => (
              <SearchedRecordChip
                key={record.reference}
                reference={record.reference}
              />
            ))}
          </ul>
        )}
      </SourceChip>
    </SourceDisclosure>
  )
}

/* One resolve per searched record in its own component: `searched` is an array
 * of N records and hooks cannot be called in a loop. A record that fails to
 * resolve renders nothing here, same as `CitedSourceExcerpt` — the count above
 * stays `searched.length` regardless, so a chip that never shows up does not
 * shrink what the caption says was searched. Each entry carries the
 * `versionId` it was read at; the chip names the record, and nothing here reads
 * the version, as with a passage's. */
function SearchedRecordChip({
  reference,
}: {
  reference: string
}): JSX.Element | null {
  const { label } = useResolvedSuggestionSource({ reference })
  if (!label) {
    return null
  }
  return (
    <li>
      <RichText inline value={sourceMentionHtml(reference, label)} />
    </li>
  )
}

/* The clinician's own words, from chat — not a clinical-record citation, so it
 * skips `useResolvedSuggestionSource` entirely: the reference names a
 * `Communication` thread, which that hook cannot resolve and which
 * `isCitableSourceReference` deliberately excludes. The quote is shown
 * unconditionally because there is no signature or chart-membership gate on a
 * clinician's own message the way there is on a clinical record.
 *
 * "Your" is literal: staged records are per-author, and the server only
 * registers the signed-in clinician's own messages as citable, so the words
 * quoted here are always the viewer's. The caption says when they were sent
 * when the provenance carries it, since a fact typed in chat is only as current
 * as the message it came from.
 *
 * A `toolCallId` means the quote is the clinician's answer to a question the
 * assistant asked, already reading "<question> → <answer>", so only the
 * framing around it changes. */
function ClinicianSourceExcerpt({
  provenance,
  className,
  onOpenSourceThread,
}: {
  provenance: ClinicianProvenance
  className?: string
  onOpenSourceThread?: OpenSourceThread
}): JSX.Element {
  const sent = clinicianSentLabel(provenance)
  return (
    <SourceDisclosure
      className={className}
      icon={IconMessage}
      triggerLabel={clinicianSourceLabel(provenance)}
      caption={
        sent
          ? `${sent}, not saved when you accept`
          : 'Not saved when you accept'
      }
      quote={provenance.quote}
    >
      <ClinicianSourceLink
        provenance={provenance}
        onOpenSourceThread={onOpenSourceThread}
      />
    </SourceDisclosure>
  )
}

/* A `toolCallId` marks the clinician's answer to a question the assistant
 * asked rather than words they typed; only the framing around the quote says
 * which. Shared by the single-entry disclosure and a listed entry, so the two
 * never describe the same provenance differently. */
function clinicianSourceLabel(provenance: ClinicianProvenance): string {
  const words =
    provenance.toolCallId === undefined
      ? 'Your words, from chat'
      : 'Your answer, from chat'
  return `${SOURCE_KIND_PREFIX[sourceKindOf(provenance)]} · ${words}`
}

function clinicianSentLabel(
  provenance: ClinicianProvenance
): string | undefined {
  if (!provenance.sentAt) {
    return undefined
  }
  const verb = provenance.toolCallId === undefined ? 'Sent' : 'Answered'
  return `${verb} ${formatDateTime(provenance.sentAt)}`
}

/* Where a clinician quote came from, in the chip's place: a button into the
 * chat turn when the caller can switch threads, plain text otherwise. */
function ClinicianSourceLink({
  provenance,
  onOpenSourceThread,
}: {
  provenance: ClinicianProvenance
  onOpenSourceThread?: OpenSourceThread
}): JSX.Element {
  return onOpenSourceThread ? (
    <Button
      className="h-auto p-0 text-xs"
      onClick={() =>
        onOpenSourceThread({
          reference: provenance.reference,
          ...(provenance.messageId && { messageId: provenance.messageId }),
        })
      }
      size="unstyled"
      variant="link"
    >
      Open in chat
    </Button>
  ) : (
    <span className="text-muted-foreground">
      {provenance.toolCallId === undefined
        ? 'From your message in chat'
        : 'From your answer in chat'}
    </span>
  )
}

/* One source's share of a list: every entry citing it, in cited order. */
interface SourceGroup {
  key: string
  entries: {
    entry: PassageProvenance | ClinicianProvenance
    position: number
  }[]
}

/* The list's entries grouped by where they came from, groups in the order each
 * source is first cited. A chart passage groups by the record it cites — the
 * same note, not the same chip text, since two notes can share a label. The
 * clinician's own words and their answers group by thread and by which of the
 * two they are, since each group carries one heading saying which. */
function groupBySource(entries: SuggestionProvenance[]): SourceGroup[] {
  const groups = new Map<string, SourceGroup>()
  entries.forEach((entry, position) => {
    // Neither groups under a source: an absence quotes nothing, and nothing
    // renders a region yet.
    if (entry.kind === 'absence' || entry.kind === 'region') {
      return
    }
    const key =
      entry.kind === 'passage'
        ? `passage:${entry.reference}`
        : `clinician:${entry.reference}:${entry.toolCallId === undefined ? 'words' : 'answer'}`
    const group = groups.get(key) ?? { key, entries: [] }
    group.entries.push({ entry, position })
    groups.set(key, group)
  })
  return [...groups.values()]
}

/* Several entries under one disclosure, grouped by source: each source's chip
 * once, with every quote taken from it under it, so three quotes from one note
 * do not repeat the note's chip three times. The caption counts what the
 * suggestion cites, not how many of the chips below manage to resolve — the
 * same rule the absence caption follows — so a passage whose record fails to
 * resolve drops out of the list without the count claiming a different
 * number of sources than the suggestion rests on.
 *
 * The disclosure itself follows the single-source rule: nothing renders — no
 * trigger, no caption — until at least one entry can be shown, so a list whose
 * records are all still resolving, unsigned or unreadable is as silent as one
 * such citation on its own. A clinician entry needs no resolving and is always
 * showable.
 *
 * An absence entry never shares a list (`sourceProvenanceOf` rejects one that
 * does), so one reaching here is skipped rather than rendered as a claim that
 * contradicts the quotes beside it.
 *
 * Quotes are keyed by position: two entries can carry the same reference and
 * quote, and a key built from them would collide. */
function SourceListExcerpt({
  entries,
  className,
  onOpenSourceThread,
}: {
  entries: SuggestionProvenance[]
  className?: string
  onOpenSourceThread?: OpenSourceThread
}): JSX.Element {
  /* Each passage's chip text once its record resolves, by position. Hooks
     cannot be called in a loop, so every passage resolves in its own
     `PassageLabel`, which reports here. Those sit outside the disclosure, so
     they stay mounted while it is folded, and the list knows whether anything
     is showable before it renders a trigger at all. */
  const [labels, setLabels] = useState<(string | undefined)[]>([])
  const reportLabel = useCallback(
    (position: number, label: string | undefined) => {
      setLabels((current) => {
        if (current[position] === label) {
          return current
        }
        const next = [...current]
        next[position] = label
        return next
      })
    },
    []
  )

  const groups = useMemo(() => groupBySource(entries), [entries])

  const items = groups.map(({ key, entries: cited }) => {
    const head = cited[0]?.entry
    if (head?.kind === 'passage') {
      // One record, so one resolution: any of its entries' labels is the chip.
      const label = cited
        .map(({ position }) => labels[position])
        .find((resolved) => resolved !== undefined)
      return label ? (
        <li className="flex flex-col gap-1" key={key}>
          <SourceChip>
            <SourceMention
              reference={head.reference}
              label={label}
              sourceKind={sourceKindOf(head)}
            />
          </SourceChip>
          <div className="flex flex-col gap-1 pl-3">
            {cited.map(({ entry, position }) => (
              <SourceQuote key={position} quote={entry.quote} />
            ))}
          </div>
        </li>
      ) : null
    }
    if (head?.kind === 'clinician') {
      return (
        <li className="flex flex-col gap-1" key={key}>
          <ClinicianEntryLabel
            provenance={head}
            showSent={cited.length === 1}
          />
          <div className="flex flex-col gap-1 pl-3">
            {cited.map(({ entry, position }) => (
              <div className="flex flex-col gap-1" key={position}>
                <SourceQuote quote={entry.quote} />
                {/* The heading already says the words are from chat, so a
                  quote only adds the way back to its own message. */}
                {onOpenSourceThread && entry.kind === 'clinician' && (
                  <SourceChip>
                    <ClinicianSourceLink
                      onOpenSourceThread={onOpenSourceThread}
                      provenance={entry}
                    />
                  </SourceChip>
                )}
              </div>
            ))}
          </div>
        </li>
      )
    }
    return null
  })

  const sources = groups.length
  return (
    <>
      {entries.map((entry, position) =>
        entry.kind === 'passage' ? (
          <PassageLabel
            // biome-ignore lint/suspicious/noArrayIndexKey: reports by position, so its identity is its position
            key={position}
            onLabel={reportLabel}
            passage={entry}
            position={position}
          />
        ) : null
      )}
      {items.some((item) => item !== null) && (
        <SourceDisclosure
          className={className}
          triggerLabel="Source excerpts"
          caption={`${entries.length} quoted from ${sources} source${sources === 1 ? '' : 's'}, not saved when you accept`}
        >
          <ul className="flex flex-col gap-2">{items}</ul>
        </SourceDisclosure>
      )}
    </>
  )
}

/* A listed clinician entry's own heading, since the list's one trigger cannot
 * carry it: the same words and time the single-entry disclosure puts on its
 * trigger and caption, so an answer still reads as an answer beside the chart
 * passages. */
function ClinicianEntryLabel({
  provenance,
  showSent,
}: {
  provenance: ClinicianProvenance
  // Only for one quote: a heading over several messages has no one time.
  showSent: boolean
}): JSX.Element {
  const sent = showSent ? clinicianSentLabel(provenance) : undefined
  return (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      <IconMessage aria-hidden className="size-3 shrink-0" />
      <span className="font-medium text-foreground">
        {clinicianSourceLabel(provenance)}
      </span>
      {sent && <span>· {sent}</span>}
    </span>
  )
}

/* Resolves one listed passage and reports its chip text — undefined until its
 * record resolves to a signed one on this chart, which keeps the passage, quote
 * and all, out of the list. Reported in a layout effect so a record already in
 * the cache shows in the same frame rather than flashing an empty list. */
function PassageLabel({
  passage,
  position,
  onLabel,
}: {
  passage: PassageProvenance
  position: number
  onLabel: (position: number, label: string | undefined) => void
}): null {
  const { label } = useResolvedSuggestionSource(passage)
  useLayoutEffect(() => {
    onLabel(position, label)
  }, [onLabel, position, label])
  return null
}

/* The shared disclosure shell every provenance kind renders through: the same
 * fold/unfold trigger and "not saved when you accept" framing, with only the
 * icon, the two lines of trigger copy, and the body's content varying per
 * kind. */
function SourceDisclosure({
  quote,
  className,
  icon: Icon = IconQuote,
  triggerLabel,
  caption,
  children,
}: {
  quote?: string
  className?: string
  icon?: TablerIcon
  triggerLabel: string
  caption: string
  children: ReactNode
}): JSX.Element {
  const [open, setOpen] = useState(true)

  return (
    <Collapsible
      className={cn('mt-2', className)}
      data-suggestion-source-excerpt=""
      onOpenChange={setOpen}
      open={open}
    >
      {/* Two lines, as the mock draws them: the icon, the label and the fold
          arrow on the first, and what an accept does with the excerpt on a
          muted second one. Both stay inside the button so the caveat is still
          part of the announced name — it is the whole reason the disclosure is
          labelled rather than left as a bare arrow. The second line is indented
          past the icon to sit under the label. */}
      <CollapsibleTrigger className="flex flex-col items-start gap-0.5 rounded-sm text-xs text-muted-foreground hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden">
        <span className="flex items-center gap-1">
          <Icon aria-hidden className="size-3 shrink-0" />
          <span className="font-semibold text-foreground">{triggerLabel}</span>
          <IconChevronRight
            aria-hidden
            className={cn(
              'size-3 shrink-0 transition-transform motion-reduce:transition-none',
              open && 'rotate-90'
            )}
          />
        </span>
        <span className="pl-4">{caption}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-1 pt-1 pl-4">
        {!!quote && <SourceQuote quote={quote} />}
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}

/* What a quote was taken from, under it: a record's chip, or where in chat a
 * clinician said it. */
function SourceChip({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="text-xs" data-suggestion-source="">
      {children}
    </div>
  )
}

/* A real `<blockquote>`, so the quotation is one to a screen reader as well as
 * to the eye. The rule and the marks are what carry it visually; it is
 * deliberately untinted, because the tint is what the proposal itself wears. */
function SourceQuote({ quote }: { quote: string }): JSX.Element {
  return (
    <blockquote className="border-l-2 border-border pl-2 text-xs leading-relaxed text-muted-foreground">
      <span aria-hidden>“</span>
      <span>{quote}</span>
      <span aria-hidden>”</span>
    </blockquote>
  )
}
